import * as ActionTypes from "../actions/types";
import { mapOAuthParametersDatum } from "../constants/map/oauth";

const INITIAL_STATE = {
  token: null,
  uuid: null,
  error: null,
  loading: true,
  fetchingInfo: false,
  redirects: {},
  oAuthParameters: null,
  manuallyLoggedOut: null,
  completedStories: [],
  userType: null,
  questionaireId: null,
  isLoggedIn: false,
  name: null,
  uid: null,
  roles: null,
  mail: null,
  loginResponse: null,
};

function login(state) {
  state.loginResponse = undefined;
  state.loading = true;
  state.manuallyLoggedOut = null;
}

function storeToken(state, action) {
  // Handle new response structure from backend
  const payload = action.payload;

  // Guard against undefined payload
  if (!payload) {
    state.loading = false;
    return;
  }

  state.name =
    payload.name ||
    `${payload.given_name || ""} ${payload.family_name || ""}`.trim() ||
    payload.email ||
    "User";
  state.uid = payload.user_id;
  state.roles = payload.roles || [];
  state.mail = payload.email;
  state.accessToken = payload.access_token;
  state.idToken = payload.id_token;
  state.tokenType = payload.token_type;
  state.expiresIn = payload.expires_in;
  state.givenName = payload.given_name;
  state.familyName = payload.family_name;
  state.isLoggedIn = true;
  state.loading = false;
}

function loginFailed(state, action) {
  const result = action.payload[0];

  // Reset to initial state
  Object.assign(state, INITIAL_STATE);
  state.isLoggedIn = false;
  state.loading = false;

  if (result === undefined) {
    state.loginResponse =
      action.payload &&
      action.payload.response &&
      action.payload.response.body &&
      action.payload.response.body.error &&
      action.payload.response.body.error.message
        ? action.payload.response.body.error.message
        : "Something went wrong, please try again later.";
  } else {
    state.loginResponse =
      result && result.rawResponse
        ? result.rawResponse
        : "Something went wrong, please try again later.";
  }
}

function storeUserDetails(state, action) {
  // Handle new response structure from backend
  const payload = action.payload;
  if (!payload) return;

  // Map new backend response to state
  state.uuid = payload.id;
  state.mail = state.mail || payload.email; // Keep existing email if already set
  state.nickname = payload.nickname;
  state.name = payload.nickname || state.name; // Set display name from nickname
  state.status = payload.status;
  state.firstName = payload.firstName;
  state.lastName = payload.lastName;
  state.company = payload.company;
  state.phone = payload.phone;
  state.langcode = payload.langcode;

  // Handle user type
  if (payload.userType) {
    state.userType = payload.userType.name;
    state.userTypeId = payload.userType.id;
  }

  // Handle user roles
  if (payload.userRoles && payload.userRoles.length > 0) {
    // If roles not already set from login, update them
    if (!state.roles || state.roles.length === 0) {
      state.roles = payload.userRoles.map((role) => role.name);
    }
  }

  // Handle image
  if (payload.image) {
    state.userImage = {
      id: payload.image.id,
      name: payload.image.imageName,
      file: payload.image.imageFile,
      alt: payload.image.altText,
    };
  }

  // completedStories is populated separately via requestFetchCompletedStories cycle
}

function updateCompletedStories(state, action) {
  state.completedStories = action.payload;
}

function fetchLoginStatusSuccess(state, action) {
  // Hydrates isLoggedIn on a cookie-session restore (e.g. page refresh),
  // since that path never goes through the interactive LOGIN_SUCCESS reducer.
  // Only handles the "authenticated" case: the "not authenticated" case is
  // already handled by logoutAfterDeniedLogin dispatching a full logout(),
  // which depends on reading the pre-update isLoggedIn value to detect an
  // expired session - forcing it false here too would race that check.
  if (action.payload === "1") {
    state.isLoggedIn = true;
  }
}

function updateUserType(state, action) {
  state.userType = action.payload.userType;
  state.questionaireId = action.payload.questionaireId;
}

function fetchUserDetailsFailed(state) {
  // No changes needed
}
function logoutFailed(state) {
  // The server logout call failed, but the user already intended to log out (and the
  // IdP end-session was triggered client-side), so never strand them logged in: clear
  // the local session just like a successful logout. The distinct LOGOUT_FAIL action
  // still surfaces the server-side failure for logging/telemetry.
  const wasManuallyLoggedOut = state.manuallyLoggedOut ? true : false;
  Object.assign(state, INITIAL_STATE);
  state.manuallyLoggedOut = wasManuallyLoggedOut;
}
function logout(state) {
  const wasManuallyLoggedOut = state.manuallyLoggedOut ? true : false;
  Object.assign(state, INITIAL_STATE);
  state.manuallyLoggedOut = wasManuallyLoggedOut;
}

function setManuallLogout(state, action) {
  state.manuallyLoggedOut = action.payload;
}

function fetchToken(state) {
  state.loading = true;
}

function fetchTokenSuccess(state, action) {
  state.token = action.payload;
  state.loading = false;
}

function fetchTokenFail(state) {
  state.loading = false;
}

function storeReturnUrl(state, action) {
  const { nonce, returnTo } = action.payload;
  if (!state.redirects) {
    state.redirects = {};
  }
  state.redirects[nonce] = returnTo;
}

function fetchOAuthParameters(state) {
  state.loading = true;
}

function fetchOAuthParametersSuccess(state, action) {
  state.oAuthParameters = mapOAuthParametersDatum(action.payload);
  state.loading = false;
}

function fetchOAuthParametersFailed(state) {
  state.loading = false;
}

export const applicationUser = {
  initialState: INITIAL_STATE,
  handlers: {
    [ActionTypes.FETCH_TOKEN]: fetchToken,
    [ActionTypes.FETCH_TOKEN_FAIL]: fetchTokenFail,
    [ActionTypes.FETCH_TOKEN_SUCCESS]: fetchTokenSuccess,
    [ActionTypes.LOGOUT_SUCCESS]: logout,
    [ActionTypes.SET_MANUAL_LOGOUT]: setManuallLogout,
    [ActionTypes.LOGOUT_FAIL]: logoutFailed,
    [ActionTypes.LOGIN]: login,
    [ActionTypes.LOGIN_SUCCESS]: storeToken,
    [ActionTypes.LOGIN_FAILED]: loginFailed,
    [ActionTypes.STORE_USER_DETAILS]: storeUserDetails,
    [ActionTypes.FETCH_USER_DETAILS_FAILED]: fetchUserDetailsFailed,
    [ActionTypes.STORE_RETURN_URL]: storeReturnUrl,
    [ActionTypes.FETCH_OAUTH_PARAMETERS]: fetchOAuthParameters,
    [ActionTypes.FETCH_OAUTH_PARAMETERS_FAILED]: fetchOAuthParametersFailed,
    [ActionTypes.FETCH_OAUTH_PARAMETERS_SUCCESS]: fetchOAuthParametersSuccess,
    [ActionTypes.UPDATE_COMPLETED_STORIES_SUCCESS]: updateCompletedStories,
    [ActionTypes.UPDATE_USER_TYPE]: updateUserType,
    [ActionTypes.FETCH_LOGIN_STATUS_SUCCESS]: fetchLoginStatusSuccess,
  },
};
